import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import packageInfo from '../../../../package.json';
import { environment } from '~environments/environment';

export interface PhraseLoaderConfig {
  locales: object;
  token: string;
  url: string;
}

export class I18nLoader extends TranslateHttpLoader {
  private static readonly cache = new Map<string, object>();

  constructor(
    private readonly httpClient: HttpClient,
    private readonly platformId: string
  ) {
    super(httpClient);
  }

  private getPhraseTranslations(lang: string): Observable<object> {
    const localeFile = `/assets/i18n/${lang}.json`;

    let filePath = this.httpClient.get(localeFile);

    if (!isPlatformBrowser(this.platformId)) {
      filePath = this.httpClient.get(
        environment.applications.front.url + localeFile
      );
    }

    return filePath;
  }

  private getRoutes(lang: string): Observable<object> {
    const isBrowser = isPlatformBrowser(this.platformId);
    const version = packageInfo.version;
    const cacheKey = `routes-${lang}-${version}`;

    if (isBrowser) {
      const existingKey = Object.keys(localStorage).find(
        (k) => k.startsWith(`routes-${lang}-`) && k !== cacheKey
      );
      if (existingKey) {
        localStorage.removeItem(existingKey);
      }
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        return of(JSON.parse(cached));
      }
    }

    let base$ = this.httpClient.get(`/assets/routes/${lang}.json`);
    if (!isBrowser) {
      base$ = this.httpClient.get(
        `${environment.applications.front.url}/assets/routes/${lang}.json`
      );
    }

    // Get translations only when version changes
    return base$.pipe(
      map((routes) => {
        const wrappedRoutes = { ROUTES: routes };

        if (isBrowser) {
          localStorage.setItem(cacheKey, JSON.stringify(wrappedRoutes));
        }

        return wrappedRoutes;
      })
    );
  }

  public getTranslation(lang: string): Observable<object> {
    const isBrowser = isPlatformBrowser(this.platformId);
    const version = packageInfo.version;
    const cacheKey = `i18n-${lang}-${version}`;

    if (isBrowser) {
      const existingKey = Object.keys(localStorage).find((key) =>
        key.startsWith(`i18n-${lang}-`)
      );

      if (existingKey && existingKey !== cacheKey) {
        localStorage.removeItem(existingKey);
      }

      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        return of(JSON.parse(cached));
      }
    } else if (I18nLoader.cache.has(lang)) {
      return of(I18nLoader.cache.get(lang));
    }

    console.log(`(v${version})`);

    // Get translations only when version changes
    return this.getPhraseTranslations(lang).pipe(
      mergeMap((translations) => {
        return this.getRoutes(lang).pipe(
          map((routes) => {
            const result = { ...translations, ...routes };

            if (isBrowser) {
              localStorage.setItem(cacheKey, JSON.stringify(result));
            } else {
              I18nLoader.cache.set(lang, result);
            }
            return result;
          })
        );
      })
    );
  }
}
